import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEntityTable } from "@mirage/store/entityTableStore";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { EntityTableHelper } from "@shared/mirage/helpers/EntityTableHelper";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, DraggableProvided, Droppable, DroppableProvided, DropResult } from "react-beautiful-dnd";
import { EntityHelper } from "../EntityTableHelper";
import Grid from "@mui/material/Grid";
import "./OrganizePredefinedViewsModal.css";
import Stack from "@mui/material/Stack";
import { PopoverColorPicker } from "@mirage/components/colorPicker/PopoverColorPicker";
import MirageFormControl from "@mirage/components/regularField/MirageFormControl";
import Field, { MirageOnChangeEvent } from "@mirage/components/regularField/Field";
import { UserDefinedButton } from "./UserDefinedButton";
import { alpha, useTheme } from "@mui/system";
import { useUserStore } from "@mirage/store/store";
import { GlobalPermissions } from "@shared/mirage/core/contracts/IPermission";
import { Typography } from "@mui/material";
import { SQLFilterConverter } from "@mirage/utilities/SqlFilterConverter";

export const OrganizePredefinedViewsModal = memo(() => {
    const { tableState, closeViewOrganizeModal, updatePredefinedView, onOrganizeViewsCancel, onOrganizeViewsSave, metadata } = useEntityTable();
    const [showNameHelpText, setShowNameHelpText] = useState(false);
    const [descriptionError, setDescriptionError] = useState("");
    const user = useUserStore((state) => state.user);
    const userPermissions = user.permissions || [];
    const hasSettingsPermission = userPermissions.findIndex((p) => p.name == GlobalPermissions.Settings) > -1;
    const [changedNames, setChangedNames] = useState<Record<string, string>>({});
    const [description, setDescription] = useState("");
    const selectedView = useMemo(
        () => tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId),
        [tableState.predefinedEntityTableViews, tableState.selectedPredefinedEntityTableViewId]
    );
    const selectedViewQuery = useMemo(() => selectedView?.clientQuery || { rules: [] }, [selectedView?.clientQuery]);
    const selectedViewName = selectedView?.name || "";
    const selectedViewDescription = selectedView?.description || "";
    const selectedViewColor = selectedView?.color || MirageTheme.palette.primary.main;
    const isAdministratorView = !!selectedView?.isAdministratorView;
    const isStatmapView = !!selectedView?.isStatmapView;
    const nameOnChange = (e: MirageOnChangeEvent, value: string | null) => {
        if (!value) {
            setShowNameHelpText(false);
            setChangedNames((prev) => {
                const updated = { ...prev };
                if (tableState.selectedPredefinedEntityTableViewId) {
                    delete updated[tableState.selectedPredefinedEntityTableViewId];
                }
                return updated;
            });
            return;
        }
        if (value.length > EntityTableHelper.MAX_VIEW_NAME_LENGTH) {
            setShowNameHelpText(true);
            return;
        }
        setShowNameHelpText(false);
        setChangedNames((prev) => ({
            ...prev,
            [tableState.selectedPredefinedEntityTableViewId]: value,
        }));
    };
    const descriptionOnChange = (e: MirageOnChangeEvent, value: string | null) => {
        if (!value) {
            setDescription("");
            setDescriptionError("");
            return;
        }
        if (value.length > EntityTableHelper.MAX_DESCRIPTION_NAME_LENGTH) {
            setDescriptionError(`Description cannot exceed ${EntityTableHelper.MAX_DESCRIPTION_NAME_LENGTH} characters.`);
            return;
        }
        setDescriptionError("");
        setDescription(value || "");
    };
    const onDialogClose = (event?: unknown, reason?: "backdropClick" | "escapeKeyDown") => {
        if (reason === "escapeKeyDown" || reason === "backdropClick") {
            return;
        }
        closeViewOrganizeModal();
    };
    const onColorChange = useCallback(
        (color: string) => {
            updatePredefinedView("color", color, tableState.selectedPredefinedEntityTableViewId);
            updatePredefinedView("colorChanged", true, tableState.selectedPredefinedEntityTableViewId);
        },
        [tableState.selectedPredefinedEntityTableViewId, updatePredefinedView]
    );
    const onIsAdminViewChange = useCallback(
        (e: MirageOnChangeEvent, value: boolean) => {
            updatePredefinedView("isAdministratorView", value, tableState.selectedPredefinedEntityTableViewId);
        },
        [tableState.selectedPredefinedEntityTableViewId, updatePredefinedView]
    );
    const mode = !isAdministratorView || hasSettingsPermission ? "edit" : "view";
    const onViewsSave = useCallback(() => {
        // Update all changed names
        Object.entries(changedNames).forEach(([viewId, viewName]) => {
            updatePredefinedView("name", viewName, viewId);
        });

        // Update description only for the currently selected view
        updatePredefinedView("description", description, tableState.selectedPredefinedEntityTableViewId);

        onOrganizeViewsSave();
    }, [changedNames, description, onOrganizeViewsSave, tableState.selectedPredefinedEntityTableViewId, updatePredefinedView]);
    const convertedQuery = useMemo(() => SQLFilterConverter.convertQueryToSQL(selectedViewQuery, metadata), [metadata, selectedViewQuery]);

    useEffect(() => {
        if (selectedView?.id) {
            setChangedNames((prev) => ({
                ...prev,
                [selectedView.id]: selectedViewName,
            }));
            setDescription(selectedViewDescription);
        }
    }, [selectedView?.id, selectedViewName, selectedViewDescription]);

    return (
        <Dialog open={tableState.viewOrganizeModalIsOpen} onClose={onDialogClose} maxWidth="lg" fullWidth>
            <DialogTitle>Organize Your views</DialogTitle>
            <DialogContent style={{ height: "700px" }}>
                <Grid container spacing={2} height="100%">
                    <Grid size={{ xs: 4 }} height="100%">
                        <ViewsList changedNames={changedNames} />
                    </Grid>
                    <Grid size={{ xs: 8 }}>
                        <Stack spacing={2}>
                            {isAdministratorView && !hasSettingsPermission ? <Typography variant="h6">This is a corporate view</Typography> : null}
                            {isStatmapView ? <Typography variant="h6">This is a statmap view</Typography> : null}
                            <Field
                                contentType="TEXT"
                                label="View Name"
                                mode={mode}
                                value={changedNames[tableState.selectedPredefinedEntityTableViewId] || ""}
                                onChange={nameOnChange}
                                errorMsg={showNameHelpText ? `Name cannot exceed ${EntityTableHelper.MAX_VIEW_NAME_LENGTH} characters.` : undefined}
                                fullWidth
                                readOnly={isStatmapView}
                            />
                            <Field
                                contentType="TEXTAREA"
                                label="Description"
                                mode={mode}
                                value={description}
                                onChange={descriptionOnChange}
                                errorMsg={descriptionError}
                                fullWidth
                                readOnly={isStatmapView}
                            />
                            <MirageFormControl label="Pick a color of the view button" mode={mode}>
                                <PopoverColorPicker color={selectedViewColor} onChange={onColorChange} mode={mode} />
                            </MirageFormControl>
                            {hasSettingsPermission && !isStatmapView ? (
                                <Field contentType="CHECKBOX" label="Admin-Defined View" mode={mode} value={isAdministratorView} onChange={onIsAdminViewChange} />
                            ) : null}
                            <Field contentType="TEXTAREA" label="Query" mode="view" value={convertedQuery} fullWidth />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onOrganizeViewsCancel} variant="outlined">
                    Cancel
                </Button>
                <Button
                    startIcon={<FontAwesomeIcon icon={MirageTheme.icons.saveIcon} style={MirageTheme.icons.sizeS} />}
                    onClick={onViewsSave}
                    variant="contained"
                    disabled={isAdministratorView && !hasSettingsPermission && !isStatmapView}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
});

interface ViewsListProps {
    changedNames: Record<string, string>;
}

const ViewsList = memo((props: ViewsListProps) => {
    const { changedNames } = props;
    const { experimentalQueryBuilder, selectPredefinedEntityTable, tableState, updatePredefinedEntityTableViewsOrder, orderedPredefinedTableViews } = useEntityTable();
    // Use the ChangedNamesContext to access changedNames from parent component
    const { predefinedEntityTableViews, selectedPredefinedEntityTableViewId } = tableState;
    const predefinedEntityTableViewsExists = Array.isArray(predefinedEntityTableViews) && predefinedEntityTableViews.length > 0;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const theme = useTheme();
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) {
                return;
            }
            const destinationIndx = result.destination.index;

            updatePredefinedEntityTableViewsOrder(result.draggableId, destinationIndx);
        },
        [updatePredefinedEntityTableViewsOrder]
    );
    const getBtnVariantStyles = (variant: "outlined" | "contained", backgroundColor?: string, colorChanged?: boolean) => {
        const btnBackgroundColor = backgroundColor ? backgroundColor : MirageTheme.palette.primary.main;

        switch (variant) {
            case "outlined":
                return {
                    borderTop: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderRight: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderBottom: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderLeft: colorChanged ? undefined : `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    backgroundColor: colorChanged ? btnBackgroundColor : "transparent",
                    color: colorChanged ? theme.palette.getContrastText(btnBackgroundColor) : "black",
                };
            case "contained":
            default:
                return {
                    color: "black",
                    backgroundColor: "transparent",
                    borderLeft: `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderRight: `1px solid ${alpha(btnBackgroundColor, 0.5)}`,
                    borderBottom: undefined,
                    borderTop: `4px solid ${btnBackgroundColor}`,
                };
        }
    };

    if (!experimentalQueryBuilder) {
        return null;
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <div ref={containerRef} style={{ height: "100%", overflow: "scroll" }}>
                <Droppable droppableId="droppable" direction="vertical">
                    {(droppableProvided: DroppableProvided) => {
                        return (
                            <div className="viewsList" ref={(ref) => droppableProvided.innerRef(ref)} {...droppableProvided.droppableProps}>
                                {predefinedEntityTableViewsExists
                                    ? orderedPredefinedTableViews.map((tableView, idx) => {
                                          const btnVariant = selectedPredefinedEntityTableViewId === tableView.id ? "contained" : "outlined";
                                          return (
                                              <Draggable key={tableView.id} draggableId={tableView.id.toString()} index={idx}>
                                                  {(provided: DraggableProvided) => {
                                                      return (
                                                          <UserDefinedButton
                                                              {...provided.dragHandleProps}
                                                              {...provided.draggableProps}
                                                              style={{
                                                                  width: "100%",
                                                                  cursor: "grab",
                                                                  borderBottomLeftRadius: "0",
                                                                  borderBottomRightRadius: "0",
                                                                  ...provided.draggableProps.style,
                                                                  ...getBtnVariantStyles(btnVariant, tableView.color, tableView.colorChanged),
                                                              }}
                                                              key={tableView.id}
                                                              ref={provided.innerRef}
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              onClick={() => selectPredefinedEntityTable(tableView.id)}
                                                          >
                                                              {EntityHelper.formatViewNameToMaxLength(changedNames[tableView.id] || tableView.name || "(none)", "toolbar")}
                                                          </UserDefinedButton>
                                                      );
                                                  }}
                                              </Draggable>
                                          );
                                      })
                                    : null}
                                {droppableProvided.placeholder}
                            </div>
                        );
                    }}
                </Droppable>
            </div>
        </DragDropContext>
    );
});
